/**
 * Campo de texto con autocompletado activo desde la base de datos.
 *
 * Es un `TextField` normal (se puede escribir cualquier valor: `freeSolo`) que,
 * a partir del tercer carácter, busca en el endpoint `/deca/sugerencias` valores
 * previos que CONTIENEN lo tecleado y los ofrece en una lista. Se usa en el alta
 * manual (Nuevo documento) y en el cambio de datos del transporte para no volver
 * a teclear un transportista, un origen, un destino o una mercancía ya conocidos.
 *
 * Para el transportista, elegir una sugerencia rellena a la vez el NIF y la razón
 * social (viajan juntos en `secundario`): eso lo resuelve el consumidor con
 * `onSeleccion`.
 */

import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import type { SxProps, Theme } from '@mui/material/styles'
import { useRef, useState } from 'react'
import { useEffect } from 'react'

import { api, type Credenciales } from '../api/cliente'
import type { Sugerencia } from '../api/tipos'

const MIN_CARACTERES = 3
const RETARDO_MS = 250

interface Props {
  label: string
  value: string
  onChange: (valor: string) => void
  /** Nombre del campo en el backend (p. ej. 'origen_poblacion', 'transportista_nif'). */
  campo: string
  /** Si se elige una sugerencia concreta (no texto libre), se recibe entera. */
  onSeleccion?: (sugerencia: Sugerencia) => void
  credenciales?: Credenciales
  required?: boolean
  fullWidth?: boolean
  maxLength?: number
  /** Pasa el texto a mayúsculas al escribir (NIF, matrícula). */
  mayusculas?: boolean
  helperText?: string
  sx?: SxProps<Theme>
}

export function CampoAutocompletar({
  label,
  value,
  onChange,
  campo,
  onSeleccion,
  credenciales,
  required,
  fullWidth,
  maxLength,
  mayusculas,
  helperText,
  sx,
}: Props) {
  const [opciones, setOpciones] = useState<Sugerencia[]>([])
  const temporizador = useRef<number | undefined>(undefined)

  // Al desmontar, cancela cualquier búsqueda pendiente.
  useEffect(() => () => window.clearTimeout(temporizador.current), [])

  function buscar(texto: string) {
    window.clearTimeout(temporizador.current)
    if (texto.trim().length < MIN_CARACTERES) {
      setOpciones([])
      return
    }
    temporizador.current = window.setTimeout(() => {
      api
        .sugerencias(campo, texto.trim(), credenciales)
        .then(setOpciones)
        .catch(() => setOpciones([]))
    }, RETARDO_MS)
  }

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      sx={sx}
      options={opciones}
      // Al enfocar, selecciona el texto: si el campo viene relleno, escribir lo
      // reemplaza y busca de inmediato en vez de concatenarse al valor previo.
      selectOnFocus
      // Se filtra en el servidor: no volver a filtrar en el cliente.
      filterOptions={(x) => x}
      inputValue={value}
      getOptionLabel={(opcion) => (typeof opcion === 'string' ? opcion : opcion.valor)}
      isOptionEqualToValue={(opcion, val) =>
        (typeof opcion === 'string' ? opcion : opcion.valor) ===
        (typeof val === 'string' ? val : val.valor)
      }
      onInputChange={(_evento, texto, motivo) => {
        const limpio = mayusculas ? texto.toUpperCase() : texto
        if (motivo === 'input') {
          onChange(limpio)
          buscar(limpio)
        }
      }}
      onChange={(_evento, seleccion) => {
        if (seleccion && typeof seleccion !== 'string') {
          if (onSeleccion) onSeleccion(seleccion)
          else onChange(seleccion.valor)
        }
      }}
      renderOption={(props, opcion) => {
        const s = opcion as Sugerencia
        return (
          <li {...props} key={`${s.valor}·${s.secundario ?? ''}`}>
            <span>
              {s.valor}
              {s.secundario ? (
                <span style={{ opacity: 0.6, marginLeft: 8 }}>· {s.secundario}</span>
              ) : null}
            </span>
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, maxLength },
          }}
        />
      )}
    />
  )
}
